import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useUserFollowing, FavoriteUser } from '@/hooks/use-favorite-users';
import { useFavoriteUserMutations } from '@/hooks/use-favorite-user-mutations';

interface AddUserSelectorProps {
  favorites: FavoriteUser[];
  onUserAdded: () => void;
}

const AddUserSelector = ({ favorites, onUserAdded }: AddUserSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: following = [], isLoading } = useUserFollowing();
  const { addFavorite } = useFavoriteUserMutations();

  // Filter out users already in favorites and apply search
  const availableUsers = following.filter(follow => {
    const isAlreadyFavorite = favorites.some(fav => fav.favorite_user_id === follow.following_id);
    if (isAlreadyFavorite) return false;
    
    if (!searchQuery) return true;
    
    const profile = Array.isArray(follow.profiles) ? follow.profiles[0] : follow.profiles;
    const searchLower = searchQuery.toLowerCase();
    return (
      profile.username.toLowerCase().includes(searchLower) ||
      profile.display_name?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddUser = (userId: string) => {
    // Find the next available position
    const nextPosition = favorites.length + 1;
    
    if (nextPosition > 20) {
      return; // Max 20 favorites
    }

    addFavorite.mutate(
      { favoriteUserId: userId, position: nextPosition },
      { onSuccess: onUserAdded }
    );
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  const canAddMore = favorites.length < 20;

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
        <Input
          placeholder="Search users you follow..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Status Message */}
      {!canAddMore && (
        <div className="text-center py-4 text-muted-foreground">
          <p>You've reached the maximum of 20 favorite users</p>
        </div>
      )}

      {/* User List */}
      <div className="max-h-80 overflow-y-auto space-y-2">
        {availableUsers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>
              {searchQuery 
                ? "No users found matching your search"
                : "No more users available to add"
              }
            </p>
          </div>
        ) : (
          availableUsers.map((follow) => {
            const profile = Array.isArray(follow.profiles) ? follow.profiles[0] : follow.profiles;
            const isAdding = addFavorite.isPending;
            
            return (
              <motion.div
                key={follow.following_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-3 p-3 bg-card border border-border rounded-lg hover:border-primary/30 transition-colors"
              >
                <Avatar className="w-10 h-10">
                  <AvatarImage src={profile.profile_image_url || undefined} />
                  <AvatarFallback>
                    {profile.username[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">
                    {profile.display_name || `@${profile.username}`}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    @{profile.username}
                  </p>
                </div>

                <Button
                  size="sm"
                  onClick={() => handleAddUser(follow.following_id)}
                  disabled={!canAddMore || isAdding}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </Button>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AddUserSelector;